import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

export type NavigationLayout = 'top' | 'sidebar';
const STORAGE_KEY = 'saas.navigationLayout';

function readLayout(): NavigationLayout {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'sidebar' ? 'sidebar' : 'top';
  } catch {
    return 'top';
  }
}

const NavigationLayoutContext = createContext<{
  layout: NavigationLayout;
  setLayout: (layout: NavigationLayout) => void;
} | null>(null);

/** 表示の好みはこのブラウザに保存。Shell と設定画面で同じ状態を使う。 */
export function NavigationLayoutProvider({ children }: { children: ReactNode }) {
  const [layout, setState] = useState<NavigationLayout>(readLayout);
  const setLayout = useCallback((next: NavigationLayout) => {
    setState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // 保存できないブラウザでも、この画面では切り替えられる。
    }
  }, []);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY || event.key === null) setState(readLayout());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return <NavigationLayoutContext.Provider value={{ layout, setLayout }}>{children}</NavigationLayoutContext.Provider>;
}

export function useNavigationLayout() {
  const value = useContext(NavigationLayoutContext);
  if (!value) throw new Error('NavigationLayoutProvider の外で呼び出されました');
  return value;
}
